'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Search, Building2, Sparkles } from 'lucide-react';
import { splanApi } from '@/lib/splan-api';
import { useTranslations } from '@/i18n/I18nProvider';

export default function RoomsPage() {
    const [search, setSearch] = useState('');
    const { t } = useTranslations('rooms');
    const { t: tCommon } = useTranslations('common');

    const { data: rooms, isLoading } = useQuery({
        queryKey: ['rooms'],
        queryFn: () => splanApi.getRooms(),
    });

    const filteredRooms = rooms?.filter(
        (room) =>
            room.name.toLowerCase().includes(search.toLowerCase()) ||
            room.building?.toLowerCase().includes(search.toLowerCase())
    );

    // Group rooms by building
    const groupedRooms = useMemo(() => {
        if (!filteredRooms) return {};
        const groups = filteredRooms.reduce(
            (acc, room) => {
                const building = room.building || 'Other';
                if (!acc[building]) acc[building] = [];
                acc[building].push(room);
                return acc;
            },
            {} as Record<string, typeof rooms>
        );
        // Sort rooms within each group
        for (const building of Object.keys(groups)) {
            groups[building]?.sort((a, b) => a.name.localeCompare(b.name));
        }
        return groups;
    }, [filteredRooms]);

    // Get unique buildings for suggestions
    const buildings = useMemo(() => {
        if (!rooms) return [];
        const buildingSet = new Set(rooms.map((r) => r.building).filter(Boolean));
        return Array.from(buildingSet).sort() as string[];
    }, [rooms]);

    // Suggested rooms when search is empty
    const suggestedRooms = useMemo(() => {
        if (!rooms || search.trim()) return [];
        // Get a sample of rooms from different buildings
        const roomsByBuilding = new Map<string, typeof rooms>();
        for (const room of rooms) {
            const building = room.building || 'Other';
            if (!roomsByBuilding.has(building)) {
                roomsByBuilding.set(building, []);
            }
            roomsByBuilding.get(building)?.push(room);
        }
        // Take first room from each building (up to 6)
        const suggestions: NonNullable<typeof rooms> = [];
        for (const [, buildingRooms] of roomsByBuilding) {
            if (suggestions.length >= 6) break;
            if (buildingRooms && buildingRooms[0]) {
                suggestions.push(buildingRooms[0]);
            }
        }
        return suggestions;
    }, [rooms, search]);

    const showSuggestions = !search.trim() && (buildings.length > 0 || suggestedRooms.length > 0);
    const sortedBuildings = Object.keys(groupedRooms).sort();

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f]">
            {/* Sub-header */}
            <div className="bg-white dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white/90 font-heading">
                        {t('roomLookup')}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-white/50">
                        {t('findRoomSchedules')}
                    </p>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search */}
                <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-white/40 w-5 h-5" />
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple/30 dark:focus:ring-purple-bright/30 focus:border-purple dark:focus:border-purple-bright/50"
                    />
                </div>

                {/* Suggestions when no search */}
                {showSuggestions && !isLoading && (
                    <div className="mb-8 space-y-6">
                        {/* Building quick filters */}
                        {buildings.length > 0 && (
                            <div className="p-4 bg-purple/5 dark:bg-purple-bright/10 rounded-lg border border-purple/10 dark:border-purple-bright/20">
                                <div className="flex items-center gap-2 mb-3">
                                    <Building2 className="w-4 h-4 text-purple dark:text-purple-bright" />
                                    <span className="text-sm font-medium text-purple dark:text-purple-bright">
                                        {t('browseByBuilding')}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {buildings.slice(0, 10).map((building) => (
                                        <button
                                            key={building}
                                            type="button"
                                            onClick={() => setSearch(building)}
                                            className="text-sm px-3 py-1.5 bg-white dark:bg-white/10 text-gray-700 dark:text-white/80 rounded-full border border-gray-200 dark:border-white/10 hover:border-purple dark:hover:border-purple-bright hover:text-purple dark:hover:text-purple-bright transition-colors"
                                        >
                                            {building}
                                        </button>
                                    ))}
                                    {buildings.length > 10 && (
                                        <span className="text-sm px-3 py-1.5 text-gray-500 dark:text-white/50">
                                            {t('moreBuildings', { count: buildings.length - 10 })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Suggested rooms as cards */}
                        {suggestedRooms.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <Sparkles className="w-5 h-5 text-purple dark:text-purple-bright" />
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white/90 font-heading">
                                        {t('suggestedRooms')}
                                    </h2>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {suggestedRooms.map((room) => (
                                        <RoomCard key={room.id} room={room} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Room List */}
                {isLoading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple dark:border-purple-bright mx-auto" />
                        <p className="mt-4 text-gray-500 dark:text-white/50">{t('loadingRooms')}</p>
                    </div>
                ) : search.trim() ? (
                    // Search results - flat list
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredRooms?.map((room) => (
                            <RoomCard key={room.id} room={room} />
                        ))}
                    </div>
                ) : (
                    // No search - grouped by building
                    <div className="space-y-8">
                        {sortedBuildings.map((building) => (
                            <div key={building}>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white/90 mb-4 font-heading flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-purple dark:text-purple-bright" />
                                    {building}
                                </h2>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {groupedRooms[building]?.map((room) => (
                                        <RoomCard key={room.id} room={room} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {filteredRooms?.length === 0 && !isLoading && (
                    <div className="text-center py-12">
                        <MapPin className="w-12 h-12 text-gray-300 dark:text-white/20 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-white/50">{t('noRooms')}</p>
                        <p className="text-sm text-gray-400 dark:text-white/40 mt-2">
                            {t('noRoomsHint')}
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}

function RoomCard({ room }: { room: { id: string; name: string; building?: string; floor?: string } }) {
    const { t: tCommon } = useTranslations('common');
    return (
        <Link
            href={`/rooms/${room.id}`}
            className="bg-white dark:bg-white/[0.02] p-4 rounded-lg border border-gray-200 dark:border-white/5 hover:border-purple/20 dark:hover:border-purple-bright/20 transition-colors"
        >
            <div className="flex items-start gap-3">
                <div className="p-2 bg-purple/10 dark:bg-purple-bright/15 rounded-lg">
                    <MapPin className="w-5 h-5 text-purple dark:text-purple-bright" />
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white/90">
                        {room.name}
                    </h3>
                    {room.building && (
                        <p className="text-sm text-gray-500 dark:text-white/50">
                            {room.building}
                            {room.floor && `, ${tCommon('floor')} ${room.floor}`}
                        </p>
                    )}
                </div>
            </div>
        </Link>
    );
}
